import { useState, useCallback, useMemo } from "react"
import { useMutation, useQuery } from "@apollo/client"
import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  NativeSelect,
  Spinner,
  Stack,
  Table,
  Text,
  VStack,
  createListCollection,
} from "@chakra-ui/react"
import { FaBan, FaEnvelope, FaEye, FaTrash } from "react-icons/fa6"
import {
  GetAllInvitationsDocument,
  GetAllOrganizationsDocument,
  CancelInvitationDocument,
  ResendInvitationDocument,
  InvitationStatus,
} from "@hydrologiq/gql-experience/graphql"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"
import { CancelButton, ConfirmButton } from "@hydrologiq/shared/content/components/Button"

const GET_ALL_INVITATIONS = GetAllInvitationsDocument
const GET_ALL_ORGANIZATIONS = GetAllOrganizationsDocument
const CANCEL_INVITATION = CancelInvitationDocument
const RESEND_INVITATION = ResendInvitationDocument

interface Invitation {
  id: string
  organizationId: string
  organizationName: string
  email: string
  role: string
  status: string
  expiresAt: string
  inviterId: string
  inviterEmail?: string
}

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "pending":
      return "orange"
    case "accepted":
      return "green"
    case "expired":
      return "red"
    case "cancelled":
      return "gray"
    default:
      return "gray"
  }
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export const InvitationManager = () => {
  const [filterOrganization, setFilterOrganization] = useState<string>("")
  const [filterStatus, setFilterStatus] = useState<string>("")
  const [filterEmail, setFilterEmail] = useState<string>("")
  const [selectedInvitation, setSelectedInvitation] = useState<Invitation | null>(null)
  const [showCancelDialog, setShowCancelDialog] = useState(false)

  // Build filter object for GraphQL query
  const filter = useMemo(() => {
    const filterObj: any = {}
    if (filterOrganization) filterObj.organizationId = filterOrganization
    if (filterStatus) filterObj.status = filterStatus.toUpperCase()
    if (filterEmail) filterObj.email = filterEmail
    return Object.keys(filterObj).length > 0 ? filterObj : null
  }, [filterOrganization, filterStatus, filterEmail])

  const {
    data: invitationsData,
    loading: invitationsLoading,
    refetch: refetchInvitations,
  } = useQuery(GET_ALL_INVITATIONS, {
    variables: { filter },
    fetchPolicy: "cache-and-network",
  })

  const { data: organizationsData } = useQuery(GET_ALL_ORGANIZATIONS)

  const [cancelInvitation, { loading: cancelling }] = useMutation(CANCEL_INVITATION, {
    onCompleted: () => {
      toaster.success({ title: "Invitation cancelled successfully" })
      setShowCancelDialog(false)
      setSelectedInvitation(null)
      refetchInvitations()
    },
    onError: (error) => {
      toaster.error({ title: "Failed to cancel invitation", description: error.message })
    },
  })

  const [resendInvitation, { loading: resending }] = useMutation(RESEND_INVITATION, {
    onCompleted: () => {
      toaster.success({ title: "Invitation resent successfully" })
      refetchInvitations()
    },
    onError: (error) => {
      toaster.error({ title: "Failed to resend invitation", description: error.message })
    },
  })

  const handleCancelInvitation = useCallback(() => {
    if (!selectedInvitation) return

    cancelInvitation({
      variables: { invitationId: selectedInvitation.id },
    })
  }, [selectedInvitation, cancelInvitation])

  const handleResendInvitation = useCallback(
    (invitation: Invitation) => {
      resendInvitation({
        variables: { invitationId: invitation.id },
      })
    },
    [resendInvitation]
  )

  const organizations = organizationsData?.getAllOrganizations || []
  const invitations = invitationsData?.getAllInvitations || []

  const organizationCollection = createListCollection({
    items: [
      { label: "All Organizations", value: "" },
      ...organizations.map((org: any) => ({ label: org.name, value: org.id })),
    ],
  })

  const statusCollection = createListCollection({
    items: [
      { label: "All Statuses", value: "" },
      { label: "Pending", value: "pending" },
      { label: "Accepted", value: "accepted" },
      { label: "Expired", value: "expired" },
      { label: "Cancelled", value: "cancelled" },
    ],
  })

  if (invitationsLoading && !invitationsData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="200px">
        <Spinner size="lg" />
      </Box>
    )
  }

  return (
    <Stack gap={6}>
      <VStack align="stretch" gap={4}>
        <Text fontSize="2xl" fontWeight="bold">
          Invitation Management
        </Text>

        {/* Filters */}
        <HStack gap={4} wrap="wrap" display={"none"}>
          <Box minW="200px">
            <NativeSelectRoot collection={organizationCollection}>
              <NativeSelectField
                placeholder="Filter by organization"
                value={filterOrganization}
                onChange={(e) => setFilterOrganization(e.target.value)}
              />
            </NativeSelectRoot>
          </Box>

          <Box minW="150px">
            <NativeSelectRoot collection={statusCollection}>
              <NativeSelectField
                placeholder="Filter by status"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              />
            </NativeSelectRoot>
          </Box>

          <Box minW="200px">
            <Input placeholder="Filter by email" value={filterEmail} onChange={(e) => setFilterEmail(e.target.value)} />
          </Box>

          <Button
            variant="outline"
            onClick={() => {
              setFilterOrganization("")
              setFilterStatus("")
              setFilterEmail("")
            }}
          >
            Clear Filters
          </Button>
        </HStack>

        {/* Stats */}
        <HStack gap={4}>
          <Text fontSize="sm">Total Invitations: {invitations.length}</Text>
          <Text fontSize="sm">Pending: {invitations.filter((inv) => inv.status === "pending").length}</Text>
          <Text fontSize="sm">Accepted: {invitations.filter((inv) => inv.status === "accepted").length}</Text>
          <Text fontSize="sm">Expired: {invitations.filter((inv) => inv.status === "expired").length}</Text>
          <Text fontSize="sm">Cancelled: {invitations.filter((inv) => inv.status === "cancelled").length}</Text>
        </HStack>
      </VStack>

      {/* Invitations Table */}
      <Box overflowX="auto">
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Email</Table.ColumnHeader>
              <Table.ColumnHeader>Organization</Table.ColumnHeader>
              <Table.ColumnHeader>Role</Table.ColumnHeader>
              <Table.ColumnHeader>Status</Table.ColumnHeader>
              <Table.ColumnHeader>Expires At</Table.ColumnHeader>
              <Table.ColumnHeader>Invited By</Table.ColumnHeader>
              <Table.ColumnHeader>Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {invitations.map((invitation) => (
              <Table.Row key={invitation.id}>
                <Table.Cell>
                  <Text fontWeight="medium">{invitation.email}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text>{invitation.organizationName}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Badge size="sm" variant="outline">
                    {invitation.role}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Badge size="sm" colorPalette={getStatusColor(invitation.status)}>
                    {invitation.status}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm">{formatDate(invitation.expiresAt)}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm">{invitation.inviterEmail || "Unknown"}</Text>
                </Table.Cell>
                <Table.Cell>
                  <HStack gap={2}>
                    {invitation.status === "pending" && (
                      <>
                        <Button
                          size="xs"
                          variant="hydrologiq_primary"
                          onClick={() => handleResendInvitation(invitation)}
                          loading={resending}
                        >
                          <FaEnvelope />
                          Resend
                        </Button>
                        <Button
                          size="xs"
                          variant="warning"
                          onClick={() => {
                            setSelectedInvitation(invitation)
                            setShowCancelDialog(true)
                          }}
                        >
                          <FaBan />
                          Cancel
                        </Button>
                      </>
                    )}
                  </HStack>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>

        {invitations.length === 0 && (
          <Box textAlign="center" py={8}>
            <Text color="gray.500">No invitations found matching the current filters.</Text>
          </Box>
        )}
      </Box>

      {/* Cancel Invitation Dialog */}
      <Dialog
        open={showCancelDialog}
        onClose={() => {
          setShowCancelDialog(false)
          setSelectedInvitation(null)
        }}
        title="Cancel Invitation"
      >
        <VStack gap={4} align="stretch">
          <Text>
            Are you sure you want to cancel the invitation to{" "}
            <Text as="span" fontWeight="bold">
              {selectedInvitation?.email}
            </Text>{" "}
            for{" "}
            <Text as="span" fontWeight="bold">
              {selectedInvitation?.organizationName}
            </Text>
            ?
          </Text>
          <Text fontSize="sm">
            This action cannot be undone. The user will no longer be able to accept this invitation.
          </Text>
          <HStack justify="flex-end" gap={2}>
            <CancelButton
              onClick={() => {
                setShowCancelDialog(false)
                setSelectedInvitation(null)
              }}
            >
              Keep Invitation
            </CancelButton>
            <ConfirmButton onClick={handleCancelInvitation} loading={cancelling}>
              Cancel invitation
            </ConfirmButton>
          </HStack>
        </VStack>
      </Dialog>
    </Stack>
  )
}
